import { useEffect } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import {
  clinicalStepsBaseline,
  editHeldHangingProtocolSelector,
  editHeldHangingProtocolState,
  getStepsFromVersioned,
  steppedHPConfigurations,
} from '../state';
import type { HangingProtocol } from '../stepUtils';
import { collectUnsavedStepEdits } from './useMirrorUnsavedStepEdits';
import { traceHPMutation } from '../hpMutationTrace';

/**
 * Decides whether a held protocol should stand in for the server match.
 *
 * Two conditions, and the first is the whole reason this is a named function with its own tests.
 *
 * **The match must have produced a protocol.** A hold may only displace a match that has actually
 * arrived and named someone else -- never one that has not answered yet. Getting this wrong is not
 * cosmetic; it is the same defect this fix exists to prevent, reached by another route. While the match
 * is in flight `matchedHangingProtocol` is `undefined`, so a bare `matched?.smid === held.smid`
 * comparison is false and the hold wins by default. On a reload that makes the hold -- a payload
 * captured at edit time, and therefore *older than the last save* -- the first thing the init pass
 * sees. It publishes those steps and claims the step-init guard, which is claimed exactly once per
 * `hpKey`, so the real match cannot re-publish when it lands: same smid, same key, guard taken. The
 * viewer then shows the pre-save protocol for the rest of the session.
 *
 * Measured: `stepped-hp-unsaved-edit-reload` phase 5 saves LATERAL, reloads, and renders PA. It failed
 * that way both with a persisted selection (racing via an extra `GET_HANGING_PROTOCOL`) and with this
 * in-memory hold (racing via the stale payload) -- which is what identifies the *stand-in during
 * loading* as the shared cause rather than either storage choice.
 *
 * A `null` match -- the server matching nothing, so the viewer falls back to the Default HP -- is
 * treated the same way, deliberately. Holding a protocol the server has stopped matching is the harm
 * VX-4905 is about.
 *
 * **And it must name a different protocol.** When the match names the held one it is the fresher copy
 * of the same thing and wins outright, so nothing here can outrank live server data for the protocol
 * it describes.
 *
 * Exported for testing.
 */
export function resolveEditHeldHangingProtocol({
  editHeldHangingProtocol,
  matchedHangingProtocol,
}: {
  editHeldHangingProtocol: Nullable<HangingProtocol>;
  matchedHangingProtocol: Nullable<HangingProtocol>;
}): Nullable<HangingProtocol> {
  if (editHeldHangingProtocol?.smid == null) return null;
  if (matchedHangingProtocol?.smid == null) return null;
  if (matchedHangingProtocol.smid === editHeldHangingProtocol.smid) return null;
  return editHeldHangingProtocol;
}

/**
 * Holds the applied protocol for this case once the reader has unsaved step edits in it, so a later
 * change in the server match cannot take it away from them (VX-4872).
 *
 * The protocol a reader is editing is often applied for no stronger reason than that it currently wins
 * `matchHangingProtocolByWorklistsAndStudies`. That match is live: it re-fires whenever the study set
 * changes, and opening a prior changes the study set. When it then lands on some other protocol, the
 * effective HP smid changes, `hpKey` changes with it, and the step init pass republishes the *new*
 * protocol's steps -- discarding the reader's unsaved edits, taking the dirty indicator with them, and
 * leaving every subsequent edit attributed to a protocol the reader never chose. That is the whole of
 * the reported bug: the protocol swaps on its own, and from then on nothing the reader does is
 * captured, which they only discover when a refresh brings back the last saved layout.
 *
 * Deliberately not armed on create. A freshly created protocol the server matched to nothing must not
 * be held, because the client cannot tell a criteria tie from a protocol that does not fit this study
 * at all, and applying the second hangs layouts on a study the server rejected them for (VX-4905). An
 * edit is a different signal: the protocol is already applied and resolved for this case, since it is
 * the one on screen -- which is also why the hold can carry the payload already in hand instead of
 * fetching one.
 *
 * Released by `clearEditHeldHangingProtocol`, called where the applied protocol genuinely changes: a
 * create/switch submit and a delete, the two places `HangingProtocolWidget` already drops the init
 * guards. Confirming a preview needs no call, because the selection it writes outranks this. Saving
 * deliberately does **not** release it -- "the SHP should remain editable until the user explicitly
 * changes or exits it" is the reported expectation, and releasing on save would hand the case back to a
 * match that has already moved, swapping the protocol out the moment the reader saved.
 *
 * See `editHeldHangingProtocolState` for why this is a separate in-memory atom rather than the
 * persisted selection: a persisted hold makes a reload race a `GET_HANGING_PROTOCOL` against the match
 * for the step-init guard, and the smaller query wins.
 */
export function useHoldHangingProtocolWithUnsavedEdits({
  caseSmid,
  hangingProtocol,
  enabled,
}: {
  caseSmid: Nullable<string>;
  hangingProtocol: Nullable<HangingProtocol>;
  enabled: boolean;
}): void {
  const versioned = useRecoilValue(steppedHPConfigurations);
  const baseline = useRecoilValue(clinicalStepsBaseline);
  const steps = getStepsFromVersioned(versioned);

  // Four instances of useHangingProtocol are mounted in a viewer and they all run this effect against
  // the same atom, so the write has to be a no-op for whichever arrives second. The comparison happens
  // inside the updater, against `prev`, and deliberately not against `snapshot.getLoadable(...)`: the
  // snapshot can be stale where `prev` cannot -- a write committed moments earlier is visible to the
  // updater and not to the snapshot, which is written up in this domain's CLAUDE.md. Reading it here
  // also released a snapshot into `recoil-sync` and surfaced as an "Snapshot has already been released"
  // unhandled error in unrelated suites.
  //
  // Returning `prev` unchanged rather than a fresh object is what makes the no-op genuinely free:
  // Recoil bails out on an identical reference, so the other three instances cause no re-render.
  const holdHangingProtocol = useRecoilCallback(
    ({ set }) =>
      (heldCaseSmid: string, protocol: NonNullable<HangingProtocol>) => {
        set(editHeldHangingProtocolState, (previous) => {
          if (previous[heldCaseSmid]?.smid === protocol.smid) return previous;
          traceHPMutation('editHold.arm', () => ({
            smid: protocol.smid ?? null,
            steps: protocol.steps?.length ?? null,
          }));
          return { ...previous, [heldCaseSmid]: protocol };
        });
      },
    []
  );

  useEffect(() => {
    if (!enabled || caseSmid == null || hangingProtocol == null) return;
    // The same divergence the dirty indicator and the persisted record are computed from, so the hold
    // arms on exactly the edits the reader can see marked. Read straight from the live atoms rather
    // than from the persisted record: the record is written by an effect in
    // `useMirrorUnsavedStepEdits`, and a match that changed before that effect ran would swap the
    // protocol out in the gap.
    if (collectUnsavedStepEdits(steps, baseline) == null) return;
    holdHangingProtocol(caseSmid, hangingProtocol);
  }, [enabled, caseSmid, hangingProtocol, steps, baseline, holdHangingProtocol]);
}

/**
 * Forgets this case's hold. For the paths where the applied protocol genuinely changes -- see the hook
 * above for which those are, and why a save is not one of them.
 */
export function useClearEditHeldHangingProtocol(caseSmid: Nullable<string>): () => void {
  return useRecoilCallback(
    ({ reset }) =>
      () => {
        if (caseSmid == null) return;
        traceHPMutation('editHold.clear');
        reset(editHeldHangingProtocolSelector(caseSmid));
      },
    [caseSmid]
  );
}
